import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ClipboardList, Calendar, AlertTriangle, CheckCircle2,
  Plus, Search, Edit2, Trash2, Clock, ArrowLeft, RefreshCw, User, ChevronRight, X, Users
} from 'lucide-react';
import { toast } from 'sonner';

// Priority badge colors
const PRIORITY_COLORS = {
  High: 'bg-red-100 text-red-700 border-red-200',
  Medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Low: 'bg-slate-100 text-slate-600 border-slate-200'
};

// Status badge colors
const STATUS_COLORS = {
  Pending: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-purple-100 text-purple-700',
  Completed: 'bg-emerald-100 text-emerald-700'
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Check if date is overdue
const isOverdue = (dateStr, status) => {
  if (!dateStr || status === 'Completed') return false;
  return new Date(dateStr) < new Date();
};

// Check if date is today
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// Format phone number for display
const formatPhone = (phone) => {
  if (!phone) return '';
  // Show last 4 digits
  return phone.length > 4 ? `...${phone.slice(-4)}` : phone;
};

const MyTasks = ({ user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ open_tasks: 0, due_today: 0, overdue: 0 });
  
  // Filters
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [search, setSearch] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'Medium',
    notes: ''
  });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (dueDate) params.due_date = dueDate;
      if (search) params.search = search;
      
      const response = await api.getMyTasks(params);
      setTasks(response.data.tasks || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [status, priority, dueDate, search]);

  const fetchSummary = async () => {
    try {
      const response = await api.getMyTasksSummary();
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchSummary();
  }, [fetchTasks]);

  // Customer search for task linking
  const searchCustomers = useCallback(async (query) => {
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setCustomerSearching(true);
    try {
      const response = await api.searchCustomersForTask(query);
      setCustomerResults(response.data.customers || []);
      setShowCustomerDropdown(true);
    } catch (error) {
      console.error('Customer search failed:', error);
      setCustomerResults([]);
    } finally {
      setCustomerSearching(false);
    }
  }, []);

  // Debounced customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      } else {
        setCustomerResults([]);
        setShowCustomerDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setCustomerResults([]);
    setShowCustomerDropdown(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      if (editingTask) {
        // For editing, check if user can edit all fields or just status/notes
        const updateData = editingTask.is_self_created 
          ? form 
          : { status: form.status, notes: form.notes };
        
        await api.updateMyTask(editingTask.task_id, updateData);
        toast.success('Task updated');
      } else {
        // Include customer data if selected
        const taskData = {
          ...form,
          customer_id: selectedCustomer?.customer_id || null,
          customer_name: selectedCustomer?.customer_name || null,
          customer_project: selectedCustomer?.project_name || null
        };
        await api.createMyTask(taskData);
        toast.success('Task created');
      }
      setDialogOpen(false);
      setEditingTask(null);
      setSelectedCustomer(null);
      setCustomerSearch('');
      setForm({ title: '', description: '', due_date: '', priority: 'Medium', notes: '' });
      fetchTasks();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      priority: task.priority || 'Medium',
      status: task.status || 'Pending',
      notes: task.notes || ''
    });
    // Restore linked customer if exists
    if (task.customer_id) {
      setSelectedCustomer({
        customer_id: task.customer_id,
        customer_name: task.customer_name || task.related_name || '',
        project_name: task.customer_project || ''
      });
    } else {
      setSelectedCustomer(null);
    }
    setCustomerSearch('');
    setDialogOpen(true);
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setForm({ title: '', description: '', due_date: '', priority: 'Medium', notes: '' });
    setSelectedCustomer(null);
    setCustomerSearch('');
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await api.deleteMyTask(taskToDelete.task_id);
      toast.success('Task deleted');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.updateMyTask(task.task_id, { status: newStatus });
      toast.success(`Task marked as ${newStatus}`);
      fetchTasks();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Quick filter handlers
  const applyQuickFilter = (filter) => {
    // Clear search when switching quick filters to avoid confusing empty results
    setSearch('');
    
    if (filter === 'all') {
      setStatus('');
      setDueDate('');
    } else if (filter === 'overdue') {
      setDueDate('overdue');
      setStatus('');
    } else if (filter === 'today') {
      setDueDate('today');
      setStatus('');
    } else if (filter === 'completed') {
      setStatus('Completed');
      setDueDate('');
    } else if (filter === 'followups') {
      // Special filter for follow-up tasks only
      setStatus('');
      setDueDate('');
      setSearch('');  // Clear search, we'll filter by task_type
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header - Standardized ← Title format */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-2 max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)} 
            className="p-1.5 -ml-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="page-back-btn"
          >
            <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-[#111827]">My Tasks</h1>
            <p className="text-xs text-slate-500">Tasks assigned to you</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card 
            className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${!status && !dueDate ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => applyQuickFilter('all')}
            data-testid="summary-open-tasks"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardList size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.open_tasks}</p>
                <p className="text-xs text-slate-500">Open</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${dueDate === 'today' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => applyQuickFilter('today')}
            data-testid="summary-due-today"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.due_today}</p>
                <p className="text-xs text-slate-500">Due Today</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${dueDate === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => applyQuickFilter('overdue')}
            data-testid="summary-overdue"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.overdue}</p>
                <p className="text-xs text-slate-500">Overdue</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Filter Chips */}
        <div className="flex flex-wrap gap-2">
          <Badge 
            variant={!status && !dueDate ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1"
            onClick={() => applyQuickFilter('all')}
          >
            All
          </Badge>
          <Badge 
            variant={dueDate === 'overdue' ? 'default' : 'outline'}
            className={`cursor-pointer px-3 py-1 ${dueDate === 'overdue' ? 'bg-red-600' : ''}`}
            onClick={() => applyQuickFilter('overdue')}
          >
            Overdue
          </Badge>
          <Badge 
            variant={dueDate === 'today' ? 'default' : 'outline'}
            className={`cursor-pointer px-3 py-1 ${dueDate === 'today' ? 'bg-amber-600' : ''}`}
            onClick={() => applyQuickFilter('today')}
          >
            Today
          </Badge>
          <Badge 
            variant={status === 'Completed' ? 'default' : 'outline'}
            className={`cursor-pointer px-3 py-1 ${status === 'Completed' ? 'bg-emerald-600' : ''}`}
            onClick={() => applyQuickFilter('completed')}
          >
            Completed
          </Badge>
          {summary.followup_tasks > 0 && (
            <Badge 
              variant="outline"
              className="cursor-pointer px-3 py-1 bg-blue-50 text-blue-700 border-blue-200"
              onClick={() => setSearch('follow')}
            >
              📌 Follow-ups ({summary.followup_tasks})
            </Badge>
          )}
        </div>

        {/* Filters and Add Button */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input 
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="task-search"
            />
          </div>
          
          <Select value={priority || "all"} onValueChange={(val) => setPriority(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={handleNewTask} data-testid="add-task-btn">
            <Plus size={16} className="mr-1" /> Add Task
          </Button>
        </div>

        {/* Tasks List */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
              <p className="text-slate-500">Loading tasks...</p>
            </div>
          ) : tasks.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle2 size={40} className="mx-auto mb-2 text-emerald-500" />
              <p className="text-slate-600">No tasks found</p>
              <p className="text-sm text-slate-400">Create a task or wait for assignments</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {tasks.map((task) => (
                <Card 
                  key={task.task_id} 
                  className={`p-4 ${isOverdue(task.due_date, task.status) ? 'border-red-200 bg-red-50' : ''}`}
                  data-testid={`task-card-${task.task_id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-slate-800">{task.title}</p>
                        {task.is_followup_task && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            📌 Follow-up
                          </Badge>
                        )}
                        {!task.is_self_created && !task.is_followup_task && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            Assigned
                          </Badge>
                        )}
                      </div>
                      {task.description && <p className="text-xs text-slate-500">{task.description}</p>}
                    </div>
                    <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                      {task.priority}
                    </Badge>
                  </div>
                  
                  {/* Linked Customer - shown for all customer-linked tasks */}
                  {(task.customer_id || (task.related_type === 'customer' && task.related_id)) && (
                    <div 
                      className="flex items-center gap-2 p-2 -mx-2 rounded-lg bg-violet-50 hover:bg-violet-100 cursor-pointer transition-colors mb-2"
                      onClick={() => navigate(`/crm/${task.customer_id || task.related_id}`)}
                      title="Click to open customer"
                      data-testid={`task-customer-link-${task.task_id}`}
                    >
                      <User size={14} className="text-violet-600" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-violet-700">{task.customer_name || task.related_name}</span>
                        {task.customer_project && (
                          <span className="text-xs text-slate-500 ml-2">• {task.customer_project}</span>
                        )}
                      </div>
                      <ChevronRight size={14} className="text-violet-400 flex-shrink-0" />
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-3">
                    <span className={`flex items-center gap-1 ${isOverdue(task.due_date, task.status) ? 'text-red-600 font-medium' : isToday(task.due_date) ? 'text-amber-600 font-medium' : ''}`}>
                      <Calendar size={12} /> {formatDate(task.due_date)}
                      {task.due_time && ` • ${task.due_time}`}
                    </span>
                    {!task.is_self_created && !task.is_followup_task && (
                      <span className="flex items-center gap-1 text-purple-600">
                        <User size={12} /> From: {task.created_by_name}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <Select value={task.status} onValueChange={(val) => handleStatusChange(task, val)}>
                      <SelectTrigger className={`h-8 w-32 text-xs ${STATUS_COLORS[task.status] || ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(task)} data-testid={`edit-task-${task.task_id}`}>
                        <Edit2 size={14} />
                      </Button>
                      {/* Only show delete for self-created tasks (not followup tasks) */}
                      {task.is_self_created && !task.is_followup_task && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600" 
                          onClick={() => { setTaskToDelete(task); setDeleteDialogOpen(true); }}
                          data-testid={`delete-task-${task.task_id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTask 
                ? (editingTask.is_self_created ? 'Edit Task' : 'Update Task Status')
                : 'New Personal Task'
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Show full form for new tasks or self-created tasks */}
            {(!editingTask || editingTask.is_self_created) && (
              <>
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Title *</label>
                  <Input 
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Task title"
                    disabled={editingTask && !editingTask.is_self_created}
                    data-testid="task-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Description</label>
                  <Textarea 
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Task description"
                    rows={2}
                    disabled={editingTask && !editingTask.is_self_created}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Due Date</label>
                    <Input 
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      disabled={editingTask && !editingTask.is_self_created}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Priority</label>
                    <Select 
                      value={form.priority} 
                      onValueChange={(val) => setForm({ ...form, priority: val })}
                      disabled={editingTask && !editingTask.is_self_created}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Customer Linking (only for new tasks) */}
                {!editingTask && (
                  <div className="relative">
                    <label className="text-sm text-slate-600 mb-1 block">
                      <span className="flex items-center gap-1.5">
                        <Users size={14} />
                        Link to Customer <span className="text-slate-400 font-normal">(optional)</span>
                      </span>
                    </label>
                    
                    {selectedCustomer ? (
                      <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <User size={16} className="text-violet-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 truncate">{selectedCustomer.customer_name}</p>
                            {selectedCustomer.project_name && (
                              <p className="text-xs text-slate-500 truncate">{selectedCustomer.project_name}</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearCustomer}
                          className="p-1 hover:bg-violet-100 rounded transition-colors flex-shrink-0"
                          data-testid="clear-customer-btn"
                        >
                          <X size={16} className="text-slate-500" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            placeholder="Search by name, phone, or project..."
                            className="pl-9"
                            data-testid="customer-search-input"
                          />
                          {customerSearching && (
                            <RefreshCw size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
                          )}
                        </div>
                        
                        {/* Customer Search Results Dropdown */}
                        {showCustomerDropdown && customerResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {customerResults.map((customer) => (
                              <button
                                key={customer.customer_id}
                                type="button"
                                onClick={() => handleSelectCustomer(customer)}
                                className="w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                                data-testid={`customer-option-${customer.customer_id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-slate-800 truncate">{customer.customer_name}</span>
                                  {customer.stage && (
                                    <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 flex-shrink-0 ml-2">
                                      {customer.stage}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                  {customer.project_name && <span className="truncate">{customer.project_name}</span>}
                                  {customer.project_name && customer.customer_phone && <span>•</span>}
                                  {customer.customer_phone && <span>{formatPhone(customer.customer_phone)}</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {showCustomerDropdown && customerSearch.length >= 2 && customerResults.length === 0 && !customerSearching && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-center">
                            <p className="text-sm text-slate-500">No customers found</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show linked customer when editing */}
                {editingTask && (editingTask.customer_id || editingTask.related_type === 'customer') && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <label className="text-xs text-slate-500 mb-1 block">Linked Customer</label>
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-violet-600" />
                      <span className="font-medium text-slate-700">
                        {editingTask.customer_name || editingTask.related_name}
                      </span>
                      {editingTask.customer_project && (
                        <span className="text-xs text-slate-500">• {editingTask.customer_project}</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Show task info for manager-assigned tasks */}
            {editingTask && !editingTask.is_self_created && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                <p className="font-medium text-purple-800">{editingTask.title}</p>
                <p className="text-sm text-purple-600">Assigned by: {editingTask.created_by_name}</p>
                {editingTask.due_date && (
                  <p className="text-sm text-purple-600">Due: {formatDate(editingTask.due_date)}</p>
                )}
              </div>
            )}
            
            {/* Status - only for editing */}
            {editingTask && (
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Notes</label>
              <Textarea 
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Add notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-task-btn">
              {editingTask ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{taskToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyTasks;
